import Channel from "./Channel";
import Device from "./Device";
import Point from "./Point";
import type DeviceTemplate from "./DeviceTemplate";
import BaseModel from "./BaseModel";
import BaseModelMap from "./BaseModelMap";
import { ConnectionType } from "./Connection";
import BaseObject from "./BaseObject";
import type Cabinet from "./Cabinet";
import ConnectableObject from "./ConnectableObject";

//////////////////////////////////////////////////
import ObjectFactory from "./ObjectFactory";
import { executePaste_Parameters } from "./BaseModel";
import Logger from "../Logger";
import Page from "./Page";
import { ChannelType, channelTypeInfo, electricalChannelType } from "./ChannelType";
import Consumer from "./Consumer";
import type Room from "./Room";
import { DragMode } from "./TreeviewModel";
import { showToast } from "../components/Toasts";
import { itx } from "../translationItx/itx";
import { DeviceClasses } from "./DeviceClasses";

ObjectFactory.registerClass(
  "Actor", // no itx
  (data?: any, parent?: BaseModel) => {
    return new Actor(data, parent);
  },
);
//////////////////////////////////////////////////
const logger = new Logger();

export default class Actor extends Device {
  public get className(): string {
    return "Actor"; // no itx
  }

  public get isActor(): boolean {
    return true;
  }

  editCounter = 0;
  eventId: string | undefined = undefined;
  numberChannelsFromUser: number | undefined = undefined;
  _isCollapsed = false;
  _classification = DeviceClasses.Actor;
  set isCollapsed(newValue: boolean) {
    this._isCollapsed = newValue;
  }
  get isCollapsed(): boolean {
    let res = this._isCollapsed;
    if (channelTypeInfo(this.channelType).isSupply) {
      res = true;
    }
    return res;
  }

  constructor(data?: any, parent?: BaseModel) {
    super(data, parent);
    this.center = new Point(0, 0);
    this._collapseMode = 1;

    this.eventId = this.connections.onChange.subscribe(this.onChangeConnections);

    if (data) {
      if (data.tag == undefined) {
        this.tag = state.nameAndTagManager.generateTag(this);
      }
      if (data.name == undefined) {
        this.name = state.translate("components.Actor", "Aktor") + ` ${this.physicalAddress}`;
      }
    }
  }

  getParentCollectionName(): string | undefined {
    return "actors"; // no itx
  }

  getPersistentProperties(): string[] {
    return ["isAutoCreated", ...super.getPersistentProperties()]; // no itx
  }

  onChangeConnections(changedObject: BaseObject, changedProperty: string | any | undefined) {
    this.applySceneConnectionsToKnxParameters();
  }

  edit(newName: string, newChannels: BaseModelMap<Channel>, newTemplate: DeviceTemplate) {
    this.editCounter++;
    this.name = newName;
    this.channels = newChannels;
    this.template = newTemplate;
    //this.channelType = newTemplate.channelType;
    this.raiseOnChange();
  }

  delete(): Array<BaseObject> {
    this.isDeleting = true;

    var res = new Array<BaseObject>();

    this.isDeleting = true;

    res = res.concat(super.delete());
    this.updateAllOtherActorColapseModes();
    return res;
  }

  getChannelTypeFromChannels(): ChannelType {
    let res = ChannelType.Unknown;
    let channelTypeArray = new Array<ChannelType>();
    this.channels?.forEach((channel) => {
      channelTypeArray.push(channel.channelType);
    });
    let countMax = 0;
    let count = 0;
    let lastChanneltype = ChannelType.Unknown;
    channelTypeArray.sort().forEach((channelType) => {
      if (channelType == lastChanneltype) {
        count++;
      } else {
        if (countMax <= count) {
          if (channelType != ChannelType.General && channelType != ChannelType.Environment) {
            res = lastChanneltype;
            countMax = count;
          }
        }
        // for next channeltype reset count and last channel type
        count = 0;
        lastChanneltype = channelType;
      }
    });
    return res;
  }

  async sortOpenChannelsToCopyOfThisActor(): Promise<undefined | Device> {
    const channelsWithoutKnxChannel = this.channels.sortBy("tag").filter((channel) => !channel.mappedChannel);

    // let copiedDevice= new Device//device.copy();
    if (channelsWithoutKnxChannel.length == 0) {
      return;
    }

    await state.menubarActions.onCopy(this);
    const copiedItems = await state.menubarActions.onPaste(this);
    let copiedDevice: undefined | Actor;
    copiedItems?.forEach((item) => {
      if (item.getIsDevice() && copiedDevice == undefined) {
        copiedDevice = item as Actor;
      }
    });

    if (copiedDevice) {
      let firstMovedChannal: Channel | undefined;
      for (let i = 0; i < channelsWithoutKnxChannel.length; i++) {
        const channel = channelsWithoutKnxChannel[i];

        const connection = channel.connections.asArray()[0];
        const partner = connection?.getConsumer();
        if (partner) {
          connection.delete();

          await copiedDevice?.treeViewModel.handleDrop2(DragMode.move, partner);
          if (firstMovedChannal == undefined) {
            firstMovedChannal = partner.getConnectedChannel();
          }
        }
      }
      if (firstMovedChannal) {
        state.selectionManager.selectedObject = firstMovedChannal;
      }
    }
    showToast({
      text: itx(
        "tabGeneralDevice.copieOfActorIsCreated",
        "Kopie des Aktors mit ehemaligen überzähligen Verbrauchern wurde erzeugt",
      ),
    });
    return copiedDevice;
  }

  findConnectableChannel(
    connectionType: ConnectionType,
    partnerObject: ConnectableObject,
    rule?: "hard" | "soft", // no itx
  ): Channel | undefined {
    var res: Channel | undefined;

    const sortedChannels = this.channelsSortedForDisplay({
      buttonChannels: false,
      actorChannels: true,
      statusChannels: false,
      logicChannels: false,
      environmentChannel: false,
      temperatureMeasurementChannel: false,
      channelWithoutKnxChannel: false,
      generalChannel: false,
      specialChannel: false,
      patchChannels: false,
      longPressChannels: false,
      manualChannel: false,
      unknownChannel: false,
      ignoreChannel: false,
    });
    sortedChannels.forEach((channel) => {
      if (channel.isConnectable(connectionType, partnerObject)) {
        if (res == undefined && channel.connections.size == 0) {
          res = channel;
        }
      }
    });

    if (res == undefined && this.isDali && partnerObject.isDali) {
      // handle dali differently (lights) all lights on same actor even if they are different channelTypes
      if (channelTypeInfo(partnerObject.channelType).isLight) {
        if (rule == "soft" ? true : this.templateId == "") {
          res = this.createNewRequestChannel(partnerObject.isDali);
          // change channeltype to partner
          res.channelType = partnerObject.channelType;
        }
      }
    }

    if (
      res == undefined &&
      !partnerObject.isDali &&
      !this.isDali &&
      (electricalChannelType(this.channelType) == electricalChannelType(partnerObject.channelType) ||
        electricalChannelType(this.getChannelTypeFromChannels()) == electricalChannelType(partnerObject.channelType) ||
        this.channelType == ChannelType.Generic)
    ) {
      if (rule == "soft" ? true : this.templateId == "") {
        if (this.channelType == ChannelType.Generic) {
          this.channelType = electricalChannelType(partnerObject.channelType);
        }
        res = this.createNewRequestChannel();
      }
    }

    if (res?.channelType == ChannelType.Generic) {
      res.channelType = electricalChannelType(partnerObject.channelType);
    }

    logger.log("findConnectableChannel", res);
    return res;
  }

  //////////////////////////////////////////////////////
  ///////////// for copy
  getExtendedCopyCollection(startItem: BaseModel): Array<BaseModel> {
    var res = new Array<BaseModel>();
    res = super.getExtendedCopyCollection(startItem);
    return res;
  }

  //////////////////////////////////////////////////////
  ///////////// for paste
  async pasteItem(params: { item: BaseModel; allItems: Array<BaseModel> }): Promise<Array<BaseModel>> {
    return (await this.parent?.pasteItem(params)) || [];
  }

  async executePaste(params: executePaste_Parameters): Promise<Array<BaseModel>> {
    const { parent, isCopyRootObj } = params;
    const cabinet = parent as Cabinet;
    var undoObjects: Array<BaseModel> = await super.executePaste(params);
    if (isCopyRootObj) {
      cabinet.actors.add(this);
      this.updateNameInPaste(cabinet.actors);
    }
    if (isCopyRootObj || state.projectMetadata.uniqueTagsForProject) {
      this.updateTagInPaste(cabinet.actors);
    }

    return undoObjects;
  }

  updateAllOtherActorColapseModes() {
    const cabinet = this.parent as Cabinet;
    if (cabinet) {
      cabinet.actors.forEach((a) => {
        const nr = cabinet.actorsColapseMode.get(a.id);
        if (nr) {
          a.collapseMode = nr;
        }
      });
    }
  }

  checkColapseMode() {
    const cabinet = this.parent as Cabinet;
    if (cabinet && state.selectionManager.selectedObject?.className == "Actor") {
      cabinet.actorsColapseMode.set(this.id, this.collapseMode);
      this.collapseMode = 0;
    }
  }

  hasConnectionOnPage(page: Page, selectedOnly: boolean): boolean {
    for (let [key, channel] of this.channels.items) {
      if (channel.hasConnectionOnPage(page, selectedOnly)) {
        return true;
      }
    }
    return false;
  }

  reSortAfterFuse() {
    function sort1(array: Array<Consumer>): Array<Consumer> {
      return array.sort((a: Consumer, b: Consumer) => {
        try {
          let nameA = a.fullTag.toUpperCase();
          let nameB = b.fullTag.toUpperCase();

          if (a.fuse) {
            nameA = a.fuse.tag + "/" + nameA;
          } else {
            nameA = "xx/" + nameA;
          }

          if (b.fuse) {
            nameB = b.fuse.tag + "/" + nameB;
          } else {
            nameB = "xx/" + nameB;
          }

          //////////////////////////////////////////////////////////////////////

          return nameA.localeCompare(nameB, undefined, {
            numeric: true,
            sensitivity: "base",
          });
        } catch (ex) {
          logger.error(`a || b undefinded`, ex);
          return 0;
        }
      });
    }

    let consumers = new Array<Consumer>();

    this.channels.forEach((channel) => {
      const consumer = channel.consumer;
      if (consumer) {
        consumers.push(consumer);
      }
    });

    consumers = sort1(consumers);
    let i = 0;
    // now set consumer to sorted tag
    this.channels.sortBy("tag").forEach((channel) => {
      const consumer = consumers[i] as Consumer;
      const page = (consumer?.parent as Room)?.page;

      channel.connections.forEach((connection) => {
        connection.delete();
      });

      if (consumer?.isConnectable(ConnectionType.Channel, channel) || consumer == undefined) {
        if (page) {
          const c = channel.connectConsumer(page, consumer);
        }

        i++;
      }
    });
  }
  /////////////////////////////////////////////////////////////////////////////////
  // if the connected channels of this actor are already connected with terminals and or fuses it should not be possible
  // -> to much confusion for user if moving is possible or not
  ///////////
  canBeMovedToNewParent(newParent: BaseModel) {
    return true;
  }

  get supplyText(): string | undefined {
    if (channelTypeInfo(this.channelType).isSupply) {
      switch (this.channelType) {
        case ChannelType.suply230V:
          return "230V"; // no itx
        case ChannelType.suply400V:
          return "400V"; // no itx
        case ChannelType.suply12V:
          return "12V"; // no itx
        case ChannelType.suply24V:
          return "24V"; // no itx
        case ChannelType.Lan:
          return "LAN";
      }
    }

    return undefined;
  }

  get fullTag(): string {
    if (channelTypeInfo(this.channelType).isSupply) {
      const Cabinet = this.parent as Cabinet;
      if (Cabinet) {
        const cabinetTag = Cabinet.fullTag;
        if (cabinetTag) {
          cabinetTag + "-";
        }
        return cabinetTag + this.supplyText;
      }
    }

    return super.fullTag;
  }

  get isConnectedToAnyFuseOrTerminal(): boolean {
    let res = false;
    for (const channel of this.channels.asArray()) {
      const consumer = channel.getConnectedConsumer();
      if (consumer) {
        if (consumer.fuse || consumer.terminal) {
          return true;
        }
      }
    }
    return false;
  }

  moveToNewParent(newParent: BaseModel): boolean {
    if (newParent.isCabinet) {
      if (this.isConnectedToAnyFuseOrTerminal) {
        showToast({
          text: itx(
            "actor.moveToNewParent.connectedToFuseOrTerminal",
            "Der Aktor ist mit einem Sicherungsautomaten oder einer Klemme verbunden, \n und kann daher nicht verschoben werden.",
          ),
        });
      }

      // "skip" handling in connectable object
      const res = BaseModel.prototype.moveToNewParent.call(this, newParent);
      return res;
    } else {
      return false;
    }
  }
}
